import React from 'react';

/**
 * Top-level error boundary.
 *
 * Without this, any render-time throw in a dashboard unmounts the whole React
 * tree and the WebView is left showing a blank white screen with no way out
 * except force-closing the app. This renders a minimal, mobile-friendly
 * fallback with a reload affordance instead.
 *
 * Deliberately plain DOM + inline styles: MUI's ThemeProvider sits INSIDE the
 * tree this boundary guards, so a theme/provider failure must not take the
 * fallback down with it.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AppErrorBoundary] Uncaught error in React tree:', error, errorInfo);
  }

  handleReload() {
    window.location.reload();
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const message =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      'An unexpected error occurred.';

    return (
      <div
        style={{
          minHeight: '100dvh',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '24px',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
          color: '#1a1a1a',
          background: '#ffffff'
        }}
      >
        <h2 style={{ margin: '0 0 12px', fontSize: '20px', fontWeight: 700 }}>
          Something went wrong
        </h2>
        <p
          style={{
            margin: '0 0 24px',
            fontSize: '14px',
            lineHeight: 1.5,
            color: '#555',
            maxWidth: '320px',
            wordBreak: 'break-word'
          }}
        >
          {message}
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            appearance: 'none',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '15px',
            fontWeight: 600,
            color: '#fff',
            background: 'linear-gradient(90deg, #C084FC 0%, #724F96 100%)',
            cursor: 'pointer',
            minWidth: '160px'
          }}
        >
          Reload app
        </button>
      </div>
    );
  }
}

export default AppErrorBoundary;
